import { WIDGET_TYPE } from 'business/constants';
import { LayoutT, WidgetTypeT } from 'business/definitions';
import { useGetConvertedSlug } from 'hooks/useGetConvertedSlug';
import { patchWidgetsPositionOnInsert } from 'lib/layout/engine/helper/layoutHelpers';
import { ContainerT, createWidget } from 'lib/layout/provider/backend';
import { TFunction, useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { WidgetT } from 'routes/Dashboard/components/WidgetsLayout/helpers';
import { HttpError } from 'store/APIClient';
import { useSelector } from 'store/hooks';

const getWidgetContentFromType = (
    type: WidgetTypeT,
    t: TFunction<'translation', undefined>,
) => {
    switch (type) {
        case WIDGET_TYPE.tableofcontent:
        case WIDGET_TYPE.separator:
        case WIDGET_TYPE.pagebreak:
            return {};
        case WIDGET_TYPE.title:
            return {
                text: t('budgetProposal.defaultTitle'),
            };
        case WIDGET_TYPE.paragraph:
            return { tiptap: '' };
        case WIDGET_TYPE.navlink:
            return {
                text: t('widget.navlink.default'),
                url: '',
            };
        default:
            throw new Error(`Unknown widget type: ${type}`);
    }
};

const useAddWidget = (
    dashboardSlug: string | null | undefined,
    publicToken?: string,
    errorCallback?: (error: HttpError) => void,
    container: ContainerT = 'dashboard',
) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const { isAuthenticated } = useSelector((state) => state.vision);

    const convertedSlug = useGetConvertedSlug(dashboardSlug);

    const query =
        publicToken && !isAuthenticated ? `?public_token=${publicToken}` : '';

    const onSuccessDashboard = (
        data: WidgetT,
        {
            position,
        }: {
            layoutId: number;
            type: WidgetTypeT;
            position: WidgetT['position'];
        },
    ) => {
        queryClient.setQueryData(
            ['getLayout', convertedSlug, query],
            (layout: LayoutT) => {
                return {
                    ...layout,
                    widgets: [
                        ...patchWidgetsPositionOnInsert(
                            layout.widgets,
                            data,
                            position,
                        ),
                        data,
                    ],
                };
            },
        );
    };

    const onSuccessArgument = (
        data: WidgetT,
        {
            position,
        }: {
            layoutId: number;
            type: WidgetTypeT;
            position: WidgetT['position'];
        },
    ) =>
        queryClient.setQueryData(
            ['widgets', data.layoutId],
            (widgets: WidgetT[]) => {
                return [
                    ...patchWidgetsPositionOnInsert(widgets, data, position),
                    data,
                ];
            },
        );

    return useMutation({
        mutationFn: ({
            layoutId,
            type,
            position,
        }: {
            position?: WidgetT['position'];
            layoutId: number;
            type: WidgetTypeT;
        }) => {
            const positionParams = {
                hasDefinedPosition: true,
                previousWidgetPosition: position,
            };
            return createWidget(container, layoutId, {
                type,
                content: getWidgetContentFromType(type, t),
                ...(position !== undefined ? positionParams : {}),
            });
        },

        onSuccess:
            container === 'dashboard' ? onSuccessDashboard : onSuccessArgument,

        onError: (error: HttpError) => {
            if (errorCallback) errorCallback(error);
        },
    });
};

export default useAddWidget;
